import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Minus, Plus, Tag, Trash2 } from 'lucide-react';
import { FulfillmentType, ReleaseStrategy } from '@/lib/shared-types';
import { decimalsFor, formatCurrency, toMoneyString } from '@/lib/i18n';
import { api } from '@/lib/api';
import { placeOrder } from '@/lib/mutations';
import { errorMessage, GUEST_FALLBACK_CURRENCY } from '@/lib/guest';
import { useGuestMenu, type GuestMenuSection } from '@/Hooks/useGuestMenu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { clearCart, setQuantity } from '@/Redux/Slices/guestSlice';

/**
 * Item ids the live menu is currently offering. An id absent from this set is
 * one the order path will refuse — sold out, withdrawn, or on a menu whose
 * schedule window has closed since it was cached (`menus.service#guestMenu`
 * filters all three out server-side, so absence is the single signal).
 */
function offeredItemIds(sections: GuestMenuSection[]): Set<string> {
  const ids = new Set<string>();
  for (const section of sections) {
    for (const item of section.items) if (!item.isSoldOut) ids.add(item.id);
  }
  return ids;
}

const RELEASE_OPTIONS = [
  ReleaseStrategy.IMMEDIATE,
  ReleaseStrategy.SCHEDULED,
  ReleaseStrategy.ON_CUSTOMER_REQUEST,
] as const;

/**
 * A seated guest may also order takeaway "for the way home" and choose
 * when the kitchen starts on it. That choice is a release strategy, not a
 * separate order type.
 */
export default function Cart() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { cart, session } = useAppSelector((s) => s.guest);
  const [fulfillment, setFulfillment] = useState<string>(FulfillmentType.DINE_IN);
  const [release, setRelease] = useState<string>(ReleaseStrategy.IMMEDIATE);
  const [scheduledFor, setScheduledFor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const [coupon, setCoupon] = useState<{ valid: boolean; text: string } | null>(null);
  // Renders from the same IndexedDB copy the menu screen used, so this costs
  // no extra wait — it is here to reconcile the cart against what is on offer.
  const { sections, refresh } = useGuestMenu();

  const currency = session?.currency ?? GUEST_FALLBACK_CURRENCY;
  const takeaway = fulfillment === FulfillmentType.TAKEAWAY;
  const needsTime = takeaway && release === ReleaseStrategy.SCHEDULED;

  /**
   * Totalled in minor units. `unitPrice * quantity` in float drifts, and the
   * scale is the currency's own — KWD has three places, JPY none — so a
   * hardcoded 2 would mis-state the subtotal we hand the coupon check. The
   * figure is display-only either way: the server prices the order,
   * and nothing below sends a price.
   */
  const scale = 10 ** decimalsFor(currency);
  const totalMinor = cart.reduce(
    (sum, line) => sum + Math.round(line.unitPrice * scale) * line.quantity,
    0,
  );
  const total = totalMinor / scale;

  /**
   * A cached menu will eventually offer something the kitchen has taken off —
   * that is the cost of rendering instantly, and the order path enforces it
   * with a 422. Catching it here means the guest is told plainly, before they
   * tap, instead of meeting a red error at the checkout. An empty `sections`
   * means we simply do not know yet (first visit, nothing cached, offline), and
   * guessing there would strike out a cart for no reason.
   */
  const offered = useMemo(() => offeredItemIds(sections), [sections]);
  const unavailable = useMemo(
    () => (sections.length ? cart.filter((line) => !offered.has(line.itemId)) : []),
    [cart, offered, sections.length],
  );

  /** A dry run against `/coupons/validate`; nothing is burned here. */
  const checkCoupon = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post<{ valid: boolean; discount: string; reason?: string }>(
        '/coupons/validate',
        { code: code.trim(), subtotal: toMoneyString(total, currency) },
      );
      setCoupon(
        data.valid
          ? {
              valid: true,
              text: t('guest.cart.couponApplied', {
                code: code.trim().toUpperCase(),
                amount: formatCurrency(data.discount, currency, i18n.language),
              }),
            }
          : { valid: false, text: data.reason || t('guest.cart.couponInvalid') },
      );
    } catch {
      setCoupon({ valid: false, text: t('guest.cart.couponInvalid') });
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Through the outbox, so a connection that drops between the tap
      // and the kitchen costs the guest a wait, not the order. `sessionId` is
      // bound from the guest token and `checkId` is deleted outright, so neither
      // is sent; nor is any price (the server prices the order).
      const { synced } = await placeOrder({
        fulfillmentType: fulfillment,
        ...(takeaway ? { releaseStrategy: release } : {}),
        // The API rejects a SCHEDULED release with no target time.
        ...(needsTime && scheduledFor
          ? { scheduledFor: new Date(scheduledFor).toISOString() }
          : {}),
        items: cart.map((line) => ({
          itemId: line.itemId,
          quantity: line.quantity,
          ...(line.notes ? { notes: line.notes } : {}),
          ...(line.modifierIds?.length ? { modifierIds: line.modifierIds } : {}),
        })),
      });
      // The order is durable either way, so the basket has genuinely left.
      dispatch(clearCart());
      toast.success(synced ? t('guest.cart.placed') : t('guest.sync.queued'));
      navigate('/track');
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;

      /**
       * 422 "sold out" / 404 "not available" is the offline-first failure: the
       * menu on screen was right when it was saved and is not right now. Pull
       * the current menu so the affected lines are marked, and say it calmly —
       * this is the system working, not breaking, and it must not look like a
       * crash to someone deciding whether to trust the app with their dinner.
       */
      if (status === 422 || status === 404) {
        const fresh = await refresh();
        const stillOffered = fresh ? offeredItemIds(fresh) : null;
        const gone = stillOffered
          ? cart.filter((line) => !stillOffered.has(line.itemId)).length
          : 0;
        if (gone > 0) toast.message(t('guest.cart.menuChanged'));
        else toast.message(errorMessage(error, t('guest.cart.failed')));
      } else {
        toast.error(errorMessage(error, t('guest.cart.failed')));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!cart.length) {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('guest.cart.empty')}</p>;
  }

  return (
    /**
     * The conventional cart: what is being bought on one side, what it costs
     * and the commitment on the other. Below `lg` it collapses to one column
     * with the summary *after* the list — a "Place the order" button sitting
     * above the items it is buying is how a guest orders the wrong thing.
     *
     * A flex column below `lg` rather than a one-column grid, because the two
     * lay a sticky descendant out differently: a grid item is constrained to
     * its own grid area, so the checkout bar would have had exactly its own
     * height to travel in and would have stopped sticking at all. A flex item
     * is constrained by the flex container's content box — the whole page —
     * which is the travel the bar has today.
     *
     * At `lg` it is a grid, and there `items-start` is load-bearing for the
     * same reason inverted: a stretched item is already the full height of its
     * row, and a sticky box that tall has nowhere left to go. Started at the
     * top it is short, and the row is as tall as the item list beside it. The
     * main track is `minmax(0,1fr)` so a long line name truncates inside the
     * column instead of widening it and scrolling the page sideways.
     */
    <div className="mx-auto flex max-w-5xl flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
      <section className="min-w-0 space-y-3" aria-labelledby="cart-items-heading">
        {unavailable.length > 0 && (
          <div className="space-y-2 rounded-lg border border-notice-line bg-notice-bg p-3 text-notice">
            <p className="text-sm font-medium">{t('guest.cart.unavailableTitle')}</p>
            <p className="text-xs">{t('guest.cart.unavailableHint')}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                for (const line of unavailable) {
                  dispatch(setQuantity({ itemId: line.itemId, quantity: 0 }));
                }
              }}
            >
              {t('guest.cart.removeUnavailable')}
            </Button>
          </div>
        )}

        <h2 id="cart-items-heading" className="text-sm font-medium text-muted-foreground">
          {t('guest.cart.itemsTitle')}
        </h2>

        <div className="space-y-2">
          {cart.map((line) => (
            <Card key={`${line.itemId}:${line.notes ?? ''}`}>
              <CardContent className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{line.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {formatCurrency(line.unitPrice, currency, i18n.language)}
                  </div>
                  {!offered.has(line.itemId) && sections.length > 0 && (
                    <div className="text-xs font-medium text-notice">
                      {t('guest.cart.unavailableLine')}
                    </div>
                  )}
                </div>

                {/* `shrink-0`: the same lesson the menu card learned. The stepper
                    is two 44px targets and must keep them at 320pt rather than
                    squeeze — the name beside it is `min-w-0` and truncates, so
                    it is the one that gives. */}
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    aria-label={line.quantity === 1 ? t('guest.cart.remove') : t('common.delete')}
                    onClick={() =>
                      dispatch(setQuantity({ itemId: line.itemId, quantity: line.quantity - 1 }))
                    }
                  >
                    {line.quantity === 1 ? (
                      <Trash2 className="h-4 w-4" />
                    ) : (
                      <Minus className="h-4 w-4" />
                    )}
                  </Button>
                  <span className="min-w-6 text-center tabular-nums">{line.quantity}</span>
                  <Button
                    size="icon"
                    variant="outline"
                    aria-label={t('common.add')}
                    onClick={() =>
                      dispatch(setQuantity({ itemId: line.itemId, quantity: line.quantity + 1 }))
                    }
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/**
        * `contents` below `lg`, a real column at and above it.
        *
        * The wrapper generates no box on a phone, so its three blocks are flex
        * items of the page itself and the checkout bar keeps the page as its
        * containing block: `sticky bottom-20` inside a wrapper only as tall as
        * its own content would pin to nothing, and the guest would lose the
        * running total they have while scrolling a long list today. At `lg` the
        * wrapper becomes the box that sticks and the bar inside it goes back to
        * being an ordinary card.
        *
        * The `max-h` + `overflow-y-auto` is the degrade: a summary taller than
        * the viewport (takeaway open, scheduled release, a coupon error) would
        * otherwise pin its head under the header and hang "Place the order"
        * below the fold with no way to scroll to it.
        */}
      <aside
        aria-label={t('guest.cart.summaryTitle')}
        className="scrollbar-thin contents lg:sticky lg:top-[4.5rem] lg:block lg:max-h-[calc(100dvh-9.5rem)] lg:space-y-4 lg:overflow-y-auto lg:overscroll-contain"
      >
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="text-sm font-medium">
              {t('guest.cart.orderType')}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[FulfillmentType.DINE_IN, FulfillmentType.TAKEAWAY].map((type) => (
                <button
                  key={type}
                  type="button"
                  aria-pressed={fulfillment === type}
                  onClick={() => setFulfillment(type)}
                  className={
                    fulfillment === type
                      ? 'rounded-lg bg-accent py-2.5 text-sm font-medium text-on-accent'
                      : 'rounded-lg border border-line-strong py-2.5 text-sm'
                  }
                >
                  {t(`order.fulfillment.${type}`)}
                </button>
              ))}
            </div>

            {takeaway && (
              <div className="space-y-1.5">
                <div className="text-xs text-muted-foreground">{t('guest.cart.whenToPrepare')}</div>
                {RELEASE_OPTIONS.map((value) => (
                  // `min-h-9`: the input is inside the label, so the whole 36px
                  // row is the target rather than the 13px dot.
                  <label key={value} className="flex min-h-9 items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="release"
                      className="shrink-0"
                      checked={release === value}
                      onChange={() => setRelease(value)}
                    />
                    {t(`guest.cart.release.${value}`)}
                  </label>
                ))}

                {needsTime && (
                  <div className="space-y-1 pt-1">
                    <label className="text-xs text-muted-foreground" htmlFor="ready-at">
                      {t('guest.cart.readyAt')}
                    </label>
                    <Input
                      id="ready-at"
                      type="datetime-local"
                      value={scheduledFor}
                      onChange={(e) => setScheduledFor(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-2 p-4">
            <label className="flex items-center gap-2 text-sm font-medium" htmlFor="coupon">
              <Tag className="h-4 w-4 shrink-0 text-accent" />
              {t('guest.cart.couponLabel')}
            </label>
            {/* At 320pt this row has 254px: 68px of "Apply" plus an 8px gap
                leaves the field 178px, which is why the field is the part that
                flexes and the button is the part that does not. */}
            <div className="flex gap-2">
              <Input
                id="coupon"
                className="flex-1"
                value={code}
                autoCapitalize="characters"
                onChange={(e) => {
                  setCode(e.target.value);
                  setCoupon(null);
                }}
                placeholder={t('guest.cart.couponPlaceholder')}
              />
              <Button
                variant="outline"
                className="shrink-0"
                disabled={!code.trim()}
                onClick={() => void checkCoupon()}
              >
                {t('guest.cart.couponApply')}
              </Button>
            </div>
            {coupon && (
              <p className={coupon.valid ? 'text-sm text-positive' : 'text-sm text-negative'}>
                {coupon.text}
              </p>
            )}
            {/* The discount is applied by the till against the check, so the
                cart shows what the code is worth rather than pre-discounting it. */}
          </CardContent>
        </Card>

        <div className="elevated sticky bottom-20 space-y-2 rounded-xl border border-line bg-card p-4 lg:static">
          <div className="flex items-baseline justify-between gap-3 font-semibold">
            <span>{t('common.total')}</span>
            <span className="tabular-nums">
              {formatCurrency(toMoneyString(total, currency), currency, i18n.language)}
            </span>
          </div>
          {/* What is shown here is the menu's price, cached or not. The
              check is priced by the server, so this is an estimate, never a quote. */}
          <p className="text-xs text-muted-foreground">{t('guest.cart.priceNote')}</p>
          <Button
            className="w-full"
            size="lg"
            disabled={submitting || (needsTime && !scheduledFor)}
            onClick={() => void submit()}
          >
            {submitting ? t('common.loading') : t('guest.cart.place')}
          </Button>
        </div>
      </aside>
    </div>
  );
}
